import type { AdyenFinalizePaymentResponse } from './adyen';
import { ProviderEnum } from './common';
import {
  FinalizeStatus,
  type BrainTreePaymentResponse,
  type NuveiPaymentResponse,
  type PaypalPaymentResponse,
  type StripePaymentResponse,
} from './payment';

export const isStripePaymentResponse = (response: unknown): response is StripePaymentResponse => {
  return Boolean(
    response && typeof response === 'object' && 'stripe' in response && typeof response.stripe === 'object'
  );
};

export const isStripeSuccessResponse = (results: unknown, provider: ProviderEnum): results is StripePaymentResponse => {
  return Boolean(
    provider === ProviderEnum.STRIPE &&
    isStripePaymentResponse(results) &&
    results.stripe.status === FinalizeStatus.SUCCESS
  );
};

export const isStripeFailedResponse = (results: unknown, provider: ProviderEnum): results is StripePaymentResponse => {
  return Boolean(
    provider === ProviderEnum.STRIPE &&
    isStripePaymentResponse(results) &&
    (results.stripe.status === FinalizeStatus.FAILED ||
      (results as StripePaymentResponse)?.appChargeErrorType !== undefined)
  );
};

export const isAdyenFinalizePaymentResponse = (results: unknown): results is AdyenFinalizePaymentResponse => {
  return Boolean(results && typeof results === 'object' && 'adyen' in results && typeof results.adyen === 'object');
};

export const isAdyenRedirectResponse = (results: AdyenFinalizePaymentResponse): boolean => {
  return results.adyen.status === FinalizeStatus.REDIRECT;
};

export const isAdyenFailedResponse = (results: AdyenFinalizePaymentResponse): boolean => {
  return results.adyen.status === FinalizeStatus.FAILED;
};

export const isAdyenSuccessResponse = (results: AdyenFinalizePaymentResponse): boolean => {
  return results.adyen.status === FinalizeStatus.SUCCESS;
};

export const isStripeRedirectResponse = (
  results: unknown,
  provider: ProviderEnum
): results is StripePaymentResponse => {
  return Boolean(
    provider === ProviderEnum.STRIPE &&
    isStripePaymentResponse(results) &&
    results.stripe.status === FinalizeStatus.REDIRECT
  );
};

export const isBrainTreePaymentResponse = (results: unknown): results is BrainTreePaymentResponse => {
  return Boolean(
    results &&
    typeof results === 'object' &&
    'braintree' in results &&
    typeof results.braintree === 'object' &&
    results.braintree &&
    'status' in results.braintree &&
    typeof results.braintree.status === 'string'
  );
};

export const isBrainTreeSuccessResponse = (
  results: unknown,
  provider: ProviderEnum
): results is BrainTreePaymentResponse => {
  return Boolean(
    provider === ProviderEnum.BRAINTREE &&
    isBrainTreePaymentResponse(results) &&
    results.braintree.status === FinalizeStatus.SUCCESS &&
    results.orderId
  );
};

export const isBrainTreeFailedResponse = (
  results: unknown,
  provider: ProviderEnum
): results is BrainTreePaymentResponse => {
  return Boolean(
    provider === ProviderEnum.BRAINTREE &&
    isBrainTreePaymentResponse(results) &&
    (results.braintree.status === FinalizeStatus.FAILED ||
      (results as BrainTreePaymentResponse)?.appChargeErrorType !== undefined)
  );
};

export const isBrainTreeRedirectResponse = (
  results: unknown,
  provider: ProviderEnum
): results is BrainTreePaymentResponse => {
  return Boolean(
    provider === ProviderEnum.BRAINTREE &&
    isBrainTreePaymentResponse(results) &&
    results.braintree.status === FinalizeStatus.REDIRECT
  );
};

export const isNuveiSuccessResponse = (results: unknown, provider: ProviderEnum): results is NuveiPaymentResponse => {
  return Boolean(
    provider === ProviderEnum.NUVEI &&
    results &&
    typeof results === 'object' &&
    'nuvei' in results &&
    typeof results.nuvei === 'object' &&
    results.nuvei &&
    'status' in results.nuvei &&
    results.nuvei.status === FinalizeStatus.SUCCESS
  );
};

export const isNuveiFailedResponse = (results: unknown, provider: ProviderEnum): results is NuveiPaymentResponse => {
  return Boolean(
    provider === ProviderEnum.NUVEI &&
    results &&
    typeof results === 'object' &&
    'nuvei' in results &&
    typeof results.nuvei === 'object' &&
    results.nuvei &&
    'status' in results.nuvei &&
    results.nuvei.status === FinalizeStatus.FAILED
  );
};

export const isNuveiRedirectResponse = (results: unknown, provider: ProviderEnum): results is NuveiPaymentResponse => {
  return Boolean(
    provider === ProviderEnum.NUVEI &&
    results &&
    typeof results === 'object' &&
    'nuvei' in results &&
    typeof results.nuvei === 'object' &&
    results.nuvei &&
    'status' in results.nuvei &&
    results.nuvei.status === FinalizeStatus.REDIRECT &&
    'sessionToken' in results
  );
};
export const isPaypalSuccessResponse = (results: unknown, provider: ProviderEnum): results is PaypalPaymentResponse => {
  return Boolean(
    provider === ProviderEnum.PAYPAL &&
    results &&
    typeof results === 'object' &&
    'paypal' in results &&
    typeof results.paypal === 'object' &&
    results.paypal &&
    'status' in results.paypal &&
    results.paypal.status === FinalizeStatus.SUCCESS
  );
};

export const isPaypalFailedResponse = (results: unknown, provider: ProviderEnum): results is PaypalPaymentResponse => {
  return Boolean(
    provider === ProviderEnum.PAYPAL &&
    results &&
    typeof results === 'object' &&
    'paypal' in results &&
    typeof results.paypal === 'object' &&
    results.paypal &&
    'status' in results.paypal &&
    results.paypal.status === FinalizeStatus.FAILED
  );
};
